import { Select } from "antd";
import { useState } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { Col, Label, Modal, ModalBody, ModalHeader, Row } from "reactstrap";
import { mergeDnhSubCategories } from "../../api/dnhCategories.api";
import GeneralButton from "../button/GeneralButton";
import { Card, CardBody } from "reactstrap";

const SelectedSubCategoryInfo = ({ subcategory }) => {
    if (!subcategory) return null;

    const infoBoxes = [
        { label: "Sub-Category Name", value: subcategory.subCategoryName },
        { label: "System ID", value: subcategory.id },
        { label: "Identifiers", value: subcategory.identifiers && subcategory.identifiers.length > 0 ? subcategory.identifiers.join(", ").toUpperCase() : "TECHMICRO" },
        { label: "Internal ID", value: subcategory._id },
    ];

    return (
        <div className="mt-2">
            <Row className="g-2">
                {infoBoxes.map((box, index) => (
                    <Col md="4" sm="6" xs="12" key={index}>
                        <Card className="shadow-sm rounded-3">
                            <CardBody className="py-2 px-3 text-center">
                                <div className="fw-bold text-primary mb-1">{box.label}</div>
                                <div className="text-muted fs-12 text-truncate">{box.value || "—"}</div>
                            </CardBody>
                        </Card>
                    </Col>
                ))}
            </Row>
        </div>
    );
};

const MergingSubCategories = ({ modal, toggle }) => {
    const { all_subCategories } = useSelector((state) => state.redux);
    const [merging, setMerging] = useState(false);
    const [fromSubCategory, setFromSubCategory] = useState(null);
    const [toSubCategory, setToSubCategory] = useState(null);
    const [confirmMerge, setConfirmMerge] = useState(false);

    const subCategoryOptions = (all_subCategories || []).map((item) => ({
        value: item._id,
        label: `${item.subCategoryName} (${item.id}) - [${item.identifiers && item.identifiers.length > 0 ? item.identifiers.join(", ").toUpperCase() : "TECHMICRO"}]`,
    }));

    const handleSelectSubCategory = (value, type) => {
        const selected = (all_subCategories || []).find((i) => i?._id === value);

        if (!selected) {
            toast.error("Selected sub-category not found.");
            return;
        }

        if (type === "from") {
            if (toSubCategory && toSubCategory._id === selected._id) {
                toast.error("You cannot select the same sub-category for both fields.");
                return;
            }
            setFromSubCategory(selected);
        } else if (type === "to") {
            if (fromSubCategory && fromSubCategory._id === selected._id) {
                toast.error("You cannot select the same sub-category for both fields.");
                return;
            }
            setToSubCategory(selected);
        }

        setConfirmMerge(false);
    };

    const handleMerge = async () => {
        if (!fromSubCategory || !toSubCategory) {
            toast.error("Please select both From and To sub-categories.");
            return;
        }

        setMerging(true);
        const loadingToast = toast.loading("Merging sub-categories...");

        await mergeDnhSubCategories(fromSubCategory._id, toSubCategory._id, (res) => {
            toast.dismiss(loadingToast);
            setMerging(false);

            if (res?.status === 200) {
                toast.success(res.data.message || "Sub-categories merged successfully!");
                setFromSubCategory(null);
                setToSubCategory(null);
                setConfirmMerge(false);
                toggle();
            } else {
                toast.error(res?.message || "Something went wrong while merging sub-categories.");
            }
        });
    };

    return (
        <Modal isOpen={modal} toggle={toggle} size="lg" centered>
            <ModalHeader toggle={toggle}>Merge DNH Sub-Categories</ModalHeader>
            <ModalBody>
                <Row>
                    <Col md={12}>
                        <Label className="fw-bold">From (Source Sub-Category)</Label>
                        <Select
                            size="large"
                            showSearch
                            placeholder="Search Sub-Category to merge FROM"
                            filterOption={(input, option) =>
                                option?.label?.toLowerCase().includes(input.toLowerCase())
                            }
                            onSelect={(value) => handleSelectSubCategory(value, "from")}
                            className="mb-2 w-100"
                            getPopupContainer={(trigger) => trigger.parentNode}
                            value={fromSubCategory ? fromSubCategory._id : undefined}
                            notFoundContent="No sub-categories found"
                            options={subCategoryOptions}
                        />
                        {fromSubCategory && <SelectedSubCategoryInfo subcategory={fromSubCategory} />}
                    </Col>

                    <Col md={12} className="mt-4">
                        <Label className="fw-bold">To (Target Sub-Category)</Label>
                        <Select
                            size="large"
                            showSearch
                            placeholder="Search Sub-Category to merge INTO"
                            filterOption={(input, option) =>
                                option?.label?.toLowerCase().includes(input.toLowerCase())
                            }
                            onSelect={(value) => handleSelectSubCategory(value, "to")}
                            className="mb-2 w-100"
                            getPopupContainer={(trigger) => trigger.parentNode}
                            value={toSubCategory ? toSubCategory._id : undefined}
                            notFoundContent="No sub-categories found"
                            options={subCategoryOptions}
                        />
                        {toSubCategory && <SelectedSubCategoryInfo subcategory={toSubCategory} />}
                    </Col>
                </Row>

                {fromSubCategory && toSubCategory && (
                    <div className="mt-4 p-3 border border-danger rounded bg-light" style={{ backgroundColor: "#fff6f6", borderColor: "#dc3545", color: "#b02a37" }}>
                        <strong>⚠️ Warning:</strong> Merging sub-categories will update all products associated with the source sub-category to the target sub-category.
                        <br />
                        This action <strong>cannot be undone</strong>.
                        <div className="form-check mt-3">
                            <input type="checkbox" id="confirmSubCategoryMerge" className="form-check-input" checked={confirmMerge} onChange={(e) => setConfirmMerge(e.target.checked)} />
                            <label htmlFor="confirmSubCategoryMerge" className="form-check-label">
                                I understand and confirm to proceed with the merge.
                            </label>
                        </div>
                    </div>
                )}

                <div className="d-flex justify-content-end mt-4">
                    <GeneralButton
                        title={"Merge Sub-Categories"}
                        className={"blue-button"}
                        width={"220px"}
                        handleClick={handleMerge}
                        disabled={merging || !confirmMerge}
                    />
                </div>
            </ModalBody>
        </Modal>
    );
};

export default MergingSubCategories;
